import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const TYPE_OPTIONS = [
  { value: 'assessment', label: 'Assessment' },
  { value: 'exam', label: 'Exam' },
  { value: 'assignment', label: 'Assignment' }
];

const TYPE_LABELS = { exam: 'Exam', assessment: 'Assessment', assignment: 'Assignment' };

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

const toDateInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const emptyDraft = () => ({
  title: '',
  type: 'assessment',
  date: '',
  startTime: '',
  endTime: '',
  location: '',
  description: ''
});

const LETTER_GRADES = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'F'];

const AssessmentsManager = ({ courseId, courseName, onBack }) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [gradingId, setGradingId] = useState(null);     // assessment _id being graded
  const [students, setStudents] = useState([]);          // enrollees [{ _id, name, email }]
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [gradeDrafts, setGradeDrafts] = useState({});    // { [studentId]: { marks, grade, comments } }
  const [savingGradeId, setSavingGradeId] = useState(null);

  const fetchAssessments = async () => {
    try {
      const { data } = await api.get(`/courses/${courseId}/assessments`);
      setAssessments(data.assessments || []);
    } catch (err) {
      setError('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledStudents = async () => {
    setStudentsLoading(true);
    try {
      const { data } = await api.get(`/enroll/course/${courseId}`);
      const list = (data.enrollments || [])
        .filter(e => e.student && e.status === 'active')
        .map(e => ({ _id: e.student._id, name: e.student.name, email: e.student.email }));
      // Dedupe by student id (in case the same person has multiple billing plans)
      const seen = new Set();
      const unique = list.filter(s => { if (seen.has(s._id)) return false; seen.add(s._id); return true; });
      setStudents(unique);
    } catch (err) {
      setError('Failed to load enrolled students');
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => { fetchAssessments(); }, [courseId]);

  const openCreate = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (assessment) => {
    setEditingId(assessment._id);
    setDraft({
      title: assessment.title || '',
      type: assessment.type || 'assessment',
      date: toDateInput(assessment.date),
      startTime: assessment.startTime || '',
      endTime: assessment.endTime || '',
      location: assessment.location || '',
      description: assessment.description || ''
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setDraft(emptyDraft());
    setError('');
  };

  const handleSave = async () => {
    if (!draft.title.trim()) { setError('Title is required'); return; }

    setSaving(true);
    setError('');
    try {
      const payload = {
        title: draft.title.trim(),
        type: draft.type,
        date: draft.date ? new Date(draft.date + 'T00:00:00').toISOString() : null,
        startTime: draft.startTime,
        endTime: draft.endTime,
        location: draft.location,
        description: draft.description
      };

      if (editingId) {
        await api.put(`/courses/${courseId}/assessments/${editingId}`, payload);
        setActionMessage('Assessment updated');
      } else {
        await api.post(`/courses/${courseId}/assessments`, payload);
        setActionMessage('Assessment added');
      }
      setTimeout(() => setActionMessage(''), 3000);
      closeModal();
      fetchAssessments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const openGrading = async (assessment) => {
    if (gradingId === assessment._id) {
      setGradingId(null);
      return;
    }
    setGradingId(assessment._id);
    if (students.length === 0) {
      await fetchEnrolledStudents();
    }
    // seed drafts from existing grades
    const seed = {};
    (assessment.grades || []).forEach(g => {
      const sid = (g.student && g.student._id) ? g.student._id.toString() : (g.student || '').toString();
      if (sid) seed[sid] = { marks: g.marks ?? '', grade: g.grade || '', comments: g.comments || '' };
    });
    setGradeDrafts(seed);
  };

  const updateGradeDraft = (studentId, field, value) => {
    setGradeDrafts(prev => ({ ...prev, [studentId]: { ...(prev[studentId] || {}), [field]: value } }));
  };

  const saveGrade = async (assessment, studentId) => {
    const d = gradeDrafts[studentId] || {};
    setSavingGradeId(studentId + ':' + assessment._id);
    setError('');
    try {
      const payload = {};
      if (assessment.type === 'exam') {
        payload.grade = d.grade || '';
      } else {
        payload.marks = d.marks === '' ? null : Number(d.marks);
      }
      payload.comments = d.comments || '';
      const { data } = await api.put(
        `/courses/${courseId}/assessments/${assessment._id}/grades/${studentId}`,
        payload
      );
      // merge saved row back into local assessment.grades
      setAssessments(prev => prev.map(a => {
        if (a._id !== assessment._id) return a;
        const grades = (a.grades || []).filter(g => {
          const sid = (g.student && g.student._id) ? g.student._id.toString() : (g.student || '').toString();
          return sid !== studentId;
        });
        grades.push(data.grade);
        return { ...a, grades };
      }));
      setActionMessage('Grade saved');
      setTimeout(() => setActionMessage(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save grade');
    } finally {
      setSavingGradeId(null);
    }
  };

  const now = Date.now();
  const unscheduled = assessments.filter(a => !a.date);
  const upcoming = assessments
    .filter(a => a.date && new Date(a.date).getTime() >= now)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = assessments
    .filter(a => a.date && new Date(a.date).getTime() < now)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  if (loading) {
    return <div className="text-gray-500">Loading assessments...</div>;
  }

  const renderCard = (assessment, isPast) => {
    const cardClasses = isPast
      ? 'bg-gray-50 border-gray-200 text-gray-500'
      : 'bg-blue-50 border-blue-200 text-blue-900';
    const badgeClasses = isPast
      ? 'bg-gray-200 text-gray-600'
      : 'bg-blue-100 text-blue-700';

    return (
      <div key={assessment._id} className={`rounded-xl border p-4 ${cardClasses}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded font-medium uppercase ${badgeClasses}`}>
                {TYPE_LABELS[assessment.type] || 'Assessment'}
              </span>
              {isPast && <span className="text-xs text-gray-500 italic">Past</span>}
            </div>
            <h3 className={`font-semibold ${isPast ? 'text-gray-700' : 'text-gray-900'} truncate`}>
              {assessment.title}
            </h3>
            <div className={`text-sm mt-1 ${isPast ? 'text-gray-500' : 'text-gray-700'}`}>
              {assessment.date
                ? formatDate(assessment.date)
                : <span className="italic text-gray-400">No date set</span>}
              {assessment.startTime && (
                <span className="ml-2">
                  · {assessment.startTime}{assessment.endTime ? ` – ${assessment.endTime}` : ''}
                </span>
              )}
            </div>
            {assessment.location && (
              <div className={`text-xs mt-1 ${isPast ? 'text-gray-400' : 'text-gray-500'}`}>
                {assessment.location}
              </div>
            )}
            {assessment.description && (
              <p className={`text-sm mt-2 ${isPast ? 'text-gray-500' : 'text-gray-700'}`}>
                {assessment.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => openGrading(assessment)}
              className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
            >
              {gradingId === assessment._id ? 'Hide Grades' : 'Grade'}
            </button>
            {!isPast && (
              <button
                onClick={() => openEdit(assessment)}
                className="text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {gradingId === assessment._id && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-xs text-gray-600 mb-3">
              {assessment.type === 'exam'
                ? 'Enter a letter grade (A+ through F) and optional comments. Each student will only see their own row.'
                : 'Enter marks out of 100 and optional comments. Each student will only see their own row.'}
            </div>
            {studentsLoading ? (
              <div className="text-sm text-gray-500">Loading students...</div>
            ) : students.length === 0 ? (
              <div className="text-sm text-gray-500 italic">No active enrolled students.</div>
            ) : (
              <div className="space-y-2">
                {students.map(stu => {
                  const sid = stu._id.toString();
                  const d = gradeDrafts[sid] || { marks: '', grade: '', comments: '' };
                  const savingKey = sid + ':' + assessment._id;
                  return (
                    <div key={sid} className="bg-white rounded-lg border border-gray-200 p-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="min-w-0 sm:w-48">
                          <div className="text-sm font-medium text-gray-900 truncate">{stu.name}</div>
                          <div className="text-xs text-gray-500 truncate">{stu.email}</div>
                        </div>
                        {assessment.type === 'exam' ? (
                          <select
                            value={d.grade}
                            onChange={e => updateGradeDraft(sid, 'grade', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent sm:w-24"
                          >
                            <option value="">—</option>
                            {LETTER_GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                        ) : (
                          <input
                            type="number"
                            min="0"
                            max="100"
                            placeholder="/ 100"
                            value={d.marks}
                            onChange={e => updateGradeDraft(sid, 'marks', e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent sm:w-24"
                          />
                        )}
                        <input
                          type="text"
                          placeholder="Comments (optional)"
                          value={d.comments}
                          onChange={e => updateGradeDraft(sid, 'comments', e.target.value)}
                          className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                        />
                        <button
                          onClick={() => saveGrade(assessment, sid)}
                          disabled={savingGradeId === savingKey}
                          className="bg-emerald-500 text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-emerald-600 disabled:bg-gray-300"
                        >
                          {savingGradeId === savingKey ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="text-base font-semibold text-brand-500 hover:text-brand-600 flex items-center gap-1.5">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Offerings
        </button>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Curriculum & Assessments</h1>
          <p className="text-sm text-gray-500 mt-1">{courseName}</p>
        </div>
        <button
          onClick={openCreate}
          className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          + Add Assessment
        </button>
      </div>

      {actionMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {actionMessage}
        </div>
      )}

      {error && !modalOpen && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {assessments.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center">
          <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">No assessments yet</h3>
          <p className="text-gray-500 mb-4">Add exams, assessments, or assignments with due dates. Students will see them in their dashboard and get email reminders.</p>
          <button
            onClick={openCreate}
            className="inline-block bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
          >
            Add First Assessment
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {unscheduled.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-3">
                Unscheduled ({unscheduled.length})
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                These assessments are part of the curriculum but have no date set yet. Click <span className="font-medium">Edit</span> to add a date — students will then see them in their dashboard.
              </p>
              <div className="space-y-3">
                {unscheduled.map(a => renderCard(a, false))}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
              Upcoming ({upcoming.length})
            </h2>
            {upcoming.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No upcoming assessments.</p>
            ) : (
              <div className="space-y-3">
                {upcoming.map(a => renderCard(a, false))}
              </div>
            )}
          </div>

          {past.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Past ({past.length})
              </h2>
              <p className="text-xs text-gray-400 mb-3">
                These assessments have passed. Click <span className="font-medium">Grade</span> to enter or update marks and comments.
              </p>
              <div className="space-y-3">
                {past.map(a => renderCard(a, true))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Assessment' : 'Add Assessment'}
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={e => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Enter assessment title"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={draft.type}
                  onChange={e => setDraft({ ...draft, type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                >
                  {TYPE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={draft.date}
                  onChange={e => setDraft({ ...draft, date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start time (optional)</label>
                  <input
                    type="time"
                    value={draft.startTime}
                    onChange={e => setDraft({ ...draft, startTime: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End time (optional)</label>
                  <input
                    type="time"
                    value={draft.endTime}
                    onChange={e => setDraft({ ...draft, endTime: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location (optional)</label>
                <input
                  type="text"
                  value={draft.location}
                  onChange={e => setDraft({ ...draft, location: e.target.value })}
                  placeholder="Enter location or leave blank for course default"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={draft.description}
                  onChange={e => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Enter instructions or details"
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>

              {editingId && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                  If you change the date, all enrolled students will receive an email notifying them of the reschedule.
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
              >
                {saving ? 'Saving...' : (editingId ? 'Save Changes' : 'Add Assessment')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentsManager;
